import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  createNewPackageRow,
  fetchPackageRow,
  updatePackageRow,
} from "../api/packageRow.api";
import {
  CreatePackageRowPayload,
  UpdatePackageRowPayload,
} from "../api/models/packageRow.models";

const usePackageRow = ({
  packageRowIdentifier,
}: {
  packageRowIdentifier: string | null;
}) => {
  const queryClient = useQueryClient();
  const [bearerToken] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken])
  );

  const {
    data: packageRow,
    error: packageRowError,
    isLoading: isLoadingPackageRow,
    refetch,
  } = useQuery({
    queryKey: ["packageRows", packageRowIdentifier],
    queryFn: () => fetchPackageRow(bearerToken as string, packageRowIdentifier),
    enabled: !!packageRowIdentifier,
  });

  const createPackageRowMutation = useMutation({
    mutationFn: (payload: CreatePackageRowPayload) =>
      createNewPackageRow(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to create package row:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packageRows"] });
    },
  });

  const updatePackageRowMutation = useMutation({
    mutationFn: (payload: UpdatePackageRowPayload) =>
      updatePackageRow(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update package row:", error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["packageRows", packageRowIdentifier],
      });
    },
  });

  const createNewPackageRowHandler = async (
    payload: CreatePackageRowPayload
  ) => {
    const result = await createPackageRowMutation.mutateAsync(payload);
    return result;
  };

  const updateExistingPackageRowHandler = async (
    payload: UpdatePackageRowPayload
  ) => {
    const result = await updatePackageRowMutation.mutateAsync(payload);
    return result;
  };

  return {
    createNewPackageRow: createNewPackageRowHandler,
    isCreatingPackageRow: createPackageRowMutation.isPending,
    updateExistingPackageRow: updateExistingPackageRowHandler,
    isUpdatingPackageRow: updatePackageRowMutation.isPending,
    createPackageRowError: createPackageRowMutation.error,
    updatePackageRowError: updatePackageRowMutation.error,
    packageRow,
    isLoadingPackageRow,
    packageRowError,
  };
};

export default usePackageRow;
